// This optional code is used to register a service worker.

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

function getServiceWorkerUrl(): string {
  if (process.env.NODE_ENV === "development") {
    return `${process.env.PUBLIC_URL}/pwa-sw.js`;
  }

  return `${process.env.PUBLIC_URL}/service-worker.js`;
}

let refreshing = false;

function reloadOnceOnControllerChange(): void {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

function activateWaitingWorker(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (!waiting) return;
  waiting.postMessage({ type: "SKIP_WAITING" });
}

export function register(config?: Config): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  reloadOnceOnControllerChange();

  window.addEventListener("load", () => {
    const swUrl = getServiceWorkerUrl();

    if (isLocalhost || process.env.NODE_ENV === "development") {
      checkValidServiceWorker(swUrl, config);
      return;
    }

    registerValidSW(swUrl, config);
  });
}

function registerValidSW(swUrl: string, config?: Config): void {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Pick up a waiting worker left from a previous visit (common after deploy).
      if (registration.waiting && navigator.serviceWorker.controller) {
        activateWaitingWorker(registration);
        if (config?.onUpdate) {
          config.onUpdate(registration);
        }
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state !== "installed") {
            return;
          }

          if (navigator.serviceWorker.controller) {
            // New content is available — activate it and reload via controllerchange.
            activateWaitingWorker(registration);
            if (config?.onUpdate) {
              config.onUpdate(registration);
            }
          } else if (config?.onSuccess) {
            config.onSuccess(registration);
          }
        };
      };

      // Check for a new build when the tab becomes visible again (kiosk / PWA).
      const checkForUpdate = () => {
        void registration.update();
      };
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          checkForUpdate();
        }
      });
      window.setInterval(checkForUpdate, 60 * 60 * 1000);
    })
    .catch((error) => {
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      registerValidSW(swUrl, config);
    });
}

export function unregister(): void {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
